<template>
  <div class="header-section">
    <h3 class="page-title">{{ title }}</h3>
    <div class="header-controls">
      <a-button
        type="primary"
        icon="clock-circle"
        class="logwork-btn"
        @click="$emit('on-logwork')"
      >
        {{ $t("logwork.buttons.logwork") }}
      </a-button>

      <a-button
        type="default"
        icon="check-circle"
        disabled
        @click="$emit('open-submit')"
      >
        {{ $t("logwork.buttons.submit") }}
      </a-button>

      <a-range-picker
        v-model="localDateRange"
        :format="dateFormat"
        :allowClear="false"
        :disabled-date="disabledDate"
        :getCalendarContainer="getPopupContainer"
        @change="handleDateChange"
        class="date-picker"
      />

      <a-select
        v-model="localViewMode"
        :options="formattedViewOptions"
        class="view-select"
        @change="handleViewChange"
      />

      <a-button disabled @click="$emit('export-excel')" icon="download">
        {{ $t("logwork.buttons.export") }}
      </a-button>
    </div>
  </div>
</template>

<script>
export default {
  name: "HeaderControls",
  props: {
    title: {
      type: String,
      default: ""
    },
    dateRange: {
      type: Array,
      required: true
    },
    dateFormat: {
      type: String,
      default: "DD/MM/YYYY"
    },
    viewMode: {
      type: String,
      default: "week",
      validator: value => ["day", "week", "month"].includes(value)
    },
    viewOptions: {
      type: Array,
      default: () => [],
      validator: value => {
        return value.every(
          option =>
            typeof option === "object" && "value" in option && "label" in option
        );
      }
    },
    getPopupContainer: {
      type: Function,
      default: () => trigger => trigger.parentNode
    }
  },
  data() {
    return {
      localDateRange: this.dateRange,
      localViewMode: this.viewMode
    };
  },
  mounted() {
    // Do not emit initial events on mount to avoid duplicate initial fetches in parent
    // Parent already performs an initial fetch in its created() hook
  },
  computed: {
    formattedViewOptions() {
      return this.viewOptions.map(option => ({
        ...option,
        label: this.$t ? this.$t(option.label) : option.label
      }));
    }
  },
  watch: {
    dateRange(val) {
      this.localDateRange = val;
    },
    viewMode(val) {
      this.localViewMode = val;
    }
  },
  methods: {
    handleDateChange(val) {
      // Validate date range
      if (!val || val.length !== 2) {
        console.warn("HeaderControls: Invalid date range selected");
        return;
      }

      const [start, end] = val;
      if (!start || !end) {
        console.warn("HeaderControls: Missing start or end date");
        return;
      }

      if (start.isAfter(end)) {
        console.warn("HeaderControls: Start date cannot be after end date");
        return;
      }

      this.$emit("update:dateRange", val);
      this.$emit("date-change", val);
    },
    handleViewChange(val) {
      this.$emit("update:viewMode", val);
      this.$emit("view-change", val);
    },

    disabledDate(current) {
      // Disable future dates
      return current && current.isAfter(new Date(), "day");
    },

    resetToDefaults() {
      // Reset to current month (can be called by parent if needed)
      const moment = this.$moment || window.moment;
      if (moment) {
        const start = moment().startOf("month");
        const end = moment();

        this.localDateRange = [start, end];
        this.handleDateChange([start, end]);
      }
    }
  }
};
</script>

<style scoped>
.header-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  border-bottom: 1px solid #e9ecef;

  .page-title {
    font-size: 21px;
    font-weight: bold;
    margin: 0;
  }
}
.header-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  button.ant-btn {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }

  .date-picker {
    width: 100px;
  }
}

.logwork-btn {
  background: #e91e63;
  border-color: #e91e63;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: #d81b60;
    border-color: #d81b60;
  }
}

.date-picker {
  min-width: 240px;

  @media (max-width: 768px) {
    min-width: 200px;
  }

  @media (max-width: 480px) {
    min-width: 180px;
  }
}

.view-select {
  min-width: 120px;

  @media (max-width: 480px) {
    min-width: 100px;
  }
}

/* Improve focus states for accessibility */
.date-picker:focus-within,
.view-select:focus-within {
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  border-color: #40a9ff;
}
</style>
